import { useEffect, useState, useRef } from 'react';
import { Box, Collapse, Grid, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import parse from 'html-react-parser';

import { ID_SITE } from '@shared/conf/env';
import useWindowSize from '@shared/hooks/useWindowsSize';
import { normalizeHtmlText } from '@shared/helpers/text/normalizeHtmlText';

export const HotelDescription = ({ descriptionHotel = {} }) => {
  const ID_SITE_CONST = Number(ID_SITE);

  const theme = useTheme();

  const [description, setDescription] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [openChecks, setOpenChecks] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const screenSize = useWindowSize();

  const descriptionRef = useRef(null);

  const amenitiesRef = useRef(null);
  const [showAmenitiesToggle, setShowAmenitiesToggle] = useState(false);

  const amenities = amenitie => {
    if (!amenitie) return '';

    let description = '';
    let value = '';

    if (amenitie.description) {
      description = String(amenitie.description);
      description = description.replaceAll('<BR />', ' ');
    }

    if (amenitie.value) {
      if (String(amenitie.value).toUpperCase() !== 'SI') {
        value = ` ${amenitie.value}`;
      }
    }

    const result = `${description}${value}`.trim();

    if (!result) return '';

    return result;
  };

  useEffect(() => {
    if (!descriptionHotel.descriptions?.length) return;

    const type = ID_SITE_CONST === 3 ? 'Location' : 'General';

    const descriptions = descriptionHotel.descriptions
      .filter(element => element.type === type)
      .map(element => element.description)
      .filter(Boolean);

    setDescription(descriptions.join('\n\n'));
  }, [descriptionHotel.descriptions, ID_SITE_CONST]);

  useEffect(() => {
    if (descriptionRef.current) {
      const element = descriptionRef.current;
      const collapsedHeight = screenSize.width > 600 ? 100 : 40;
      if (element.scrollHeight > collapsedHeight + 5) {
        setShowToggle(true);
      }
    }
  }, [description, screenSize.width]);

  useEffect(() => {
    if (!amenitiesRef.current) return;

    const el = amenitiesRef.current;

    const collapsedHeight = screenSize.width > 600 ? 100 : 60;

    setShowAmenitiesToggle(el.scrollHeight > collapsedHeight + 5);
  }, [descriptionHotel.amenities, screenSize.width]);

  if (!description?.length && !descriptionHotel?.amenities?.length) {
    return (
      <Grid
        container
        spacing={2}
        sx={{
          width: '95%',
          backgroundColor: '#F6F6F6',
          borderRadius: '8px',
          px: '8px',
          mb: '3vh',
        }}
      >
        <Grid item xs={12}>
          <Typography
            sx={{
              fontSize: { xs: '18px', sm: '20px' },
              color: '#000',
              fontWeight: 600,
              mb: '10px',
            }}
          >
            Descripción:
          </Typography>

          <Collapse in={open} collapsedSize={100}>
            <Typography
              sx={{
                fontSize: '16px',
                color: '#000',
                overflowWrap: 'break-word',
              }}
            >
              No disponible
            </Typography>
          </Collapse>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={2}
      sx={{
        width: '100%',
        backgroundColor: '#F6F6F6',
        borderRadius: '8px',
        px: '8px',
        pb: '8px',
        ml: 0,
      }}
    >
      {description?.length > 0 && (
        <Grid item xs={12}>
          <Typography
            sx={{
              fontSize: { xs: '18px', sm: '20px' },
              color: '#000',
              fontWeight: 600,
              mb: '10px',
            }}
          >
            Descripción:
          </Typography>

          <Collapse in={open} collapsedSize={screenSize.width > 600 ? 100 : 40}>
            <Typography
              component="div"
              ref={descriptionRef}
              sx={{
                fontSize: '16px',
                overflowWrap: 'break-word',
                ...(open || !showToggle
                  ? { color: '#000' }
                  : {
                      background:
                        'linear-gradient(to bottom, black, gray, rgb(252,241,241), rgb(253,247,247))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }),
              }}
            >
              {parse(normalizeHtmlText(description))}
            </Typography>
          </Collapse>

          {showToggle && (
            <Box
              sx={{
                mt: screenSize.width > 600 ? '2vh' : 0,
                cursor: 'pointer',
                display: 'flex',
                color: theme?.tertiary,
                fontSize: '15px',
                fontFamily: 'Open Sans, sans-serif',
              }}
              onClick={() => setOpen(!open)}
            >
              {open ? 'Ver menos' : 'Ver más'}
              {open ? (
                <ExpandLessIcon sx={{ ml: '0.2vw', mt: '-1px' }} />
              ) : (
                <ExpandMoreIcon sx={{ ml: '0.2vw', mt: '-1px' }} />
              )}
            </Box>
          )}
        </Grid>
      )}

      {descriptionHotel.amenities.length > 0 && (
        <>
          {description?.length > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  height: '1px',
                  backgroundColor: '#D1D1D1',
                  width: '100%',
                }}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography
              sx={{
                fontSize: { xs: '18px', sm: '20px' },
                color: '#000',
                fontWeight: 600,
                mb: '10px',
              }}
            >
              El alojamiento ofrece:
            </Typography>

            <Collapse
              in={openChecks}
              collapsedSize={screenSize.width > 600 ? 100 : 60}
            >
              <div ref={amenitiesRef}>
                <Grid container>
                  {descriptionHotel.amenities.map((d, i) => (
                    <Grid
                      key={+i}
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        fontFamily: 'Open Sans, sans-serif',
                        fontSize: '16px',
                        mb: 1,
                      }}
                    >
                      <CheckIcon
                        sx={{
                          fontSize: '16px',
                          position: 'relative',
                          top: '3px',
                          mr: '5px',
                        }}
                      />
                      <Typography sx={{ fontSize: '16px', color: '#000' }}>
                        {amenities(d)}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </div>
            </Collapse>

            {showAmenitiesToggle && (
              <Box
                sx={{
                  mt: '2vh',
                  cursor: 'pointer',
                  display: 'flex',
                  color: theme?.tertiary,
                  fontSize: '15px',
                  fontFamily: 'Open Sans, sans-serif',
                }}
                onClick={() => setOpenChecks(prev => !prev)}
              >
                {openChecks ? 'Ver menos' : 'Ver más'}
                {openChecks ? (
                  <ExpandLessIcon sx={{ ml: '0.2vw', mt: '-1px' }} />
                ) : (
                  <ExpandMoreIcon sx={{ ml: '0.2vw', mt: '-1px' }} />
                )}
              </Box>
            )}
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default HotelDescription;
